import React, { useEffect, useState } from 'react';
import { ErrorMessage, Formik } from 'formik';
import Swal from "sweetalert2";
import { useNavigate } from 'react-router-dom';
import * as emailTemplateService from '../../../services/email.template';
import Breadcrums from '../../../common/breadcrumbs';
import CustomCkeditor from '../../../common/customeditor';
import { EMAIL_TEMPLATE_TYPES, SWAL_SETTINGS } from '../../../../../utils/Constants';
import { globalLoader, handleServerValidations } from '../../../../../utils/commonfunction';
import * as Yup from 'yup';
import { useLocation } from 'react-router-dom';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: 'gb' },
  { code: 'de', name: 'German', flag: 'de' },
  { code: 'hu', name: 'Hungarian', flag: 'hu' },
  { code: 'es', name: 'Spanish', flag: 'es' },
];

const EmailTempAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitted, setSubmitted] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const devParam = queryParams.get('dev');

  const breadcrumbs = [
    { title: "Dashboard", url: "/dashboard" },
    { title: "Automatic Email", url: "/admin/cms/default-email-template/list/1" },
    { title: "Add", url: "" },
  ];

  useEffect(() => {
    if (devParam !== 'true') {
      navigate('/admin/cms/default-email-template/list/1');
    }
  }, [devParam, navigate]);

  // Validation Schema for 4 Languages
  const validationSchema = Yup.object().shape({
    slug: Yup.string()
      .required("Slug is required")
      .matches(/^[a-z0-9-_]+$/, "Only lowercase letters, numbers, hyphens, and underscores allowed"),

    // Title
    title_en: Yup.string().required("English title is required").test('trim', 'Required', v => v?.trim()),
    title_de: Yup.string().required("German title is required").test('trim', 'Required', v => v?.trim()),
    title_hu: Yup.string().required("Hungarian title is required").test('trim', 'Required', v => v?.trim()),
    title_es: Yup.string().required("Spanish title is required").test('trim', 'Required', v => v?.trim()),

    // Subject
    subject_en: Yup.string().required("English subject is required").test('trim', 'Required', v => v?.trim()),
    subject_de: Yup.string().required("German subject is required").test('trim', 'Required', v => v?.trim()),
    subject_hu: Yup.string().required("Hungarian subject is required").test('trim', 'Required', v => v?.trim()),
    subject_es: Yup.string().required("Spanish subject is required").test('trim', 'Required', v => v?.trim()),

    // Description (CKEditor)
    description_en: Yup.string().required("English description is required").min(10, "Too short"),
    description_de: Yup.string().required("German description is required").min(10, "Too short"),
    description_hu: Yup.string().required("Hungarian description is required").min(10, "Too short"),
    description_es: Yup.string().required("Spanish description is required").min(10, "Too short"),
  });

  const initialValues = {
    slug: "",
    title_en: "", title_de: "", title_hu: "", title_es: "",
    subject_en: "", subject_de: "", subject_hu: "", subject_es: "",
    description_en: "", description_de: "", description_hu: "", description_es: "",
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitted(true);
          globalLoader(true);

          const formData = new FormData();
          formData.append('type', EMAIL_TEMPLATE_TYPES.DEFAULT);
          formData.append('slug', values.slug);

          // Append all language fields
          LANGUAGES.forEach(lang => {
            formData.append(`title_${lang.code}`, values[`title_${lang.code}`]);
            formData.append(`subject_${lang.code}`, values[`subject_${lang.code}`]);
            formData.append(`description_${lang.code}`, values[`description_${lang.code}`]);
          });

          emailTemplateService.Add(formData)
            .then(response => {
              if (response.success) {
                Swal.fire({ icon: 'success', text: response.message, ...SWAL_SETTINGS });
                setTimeout(() => navigate('/admin/cms/default-email-template/list/1'), 2000);
              } else {
                Swal.fire({ icon: 'error', text: handleServerValidations(response), ...SWAL_SETTINGS });
                setSubmitted(false);
              }
            })
            .catch(err => {
              console.error(err);
              Swal.fire({ icon: 'error', text: "Something went wrong!", ...SWAL_SETTINGS });
              setSubmitted(false);
            })
            .finally(() => {
              setSubmitting(false);
              globalLoader(false);
            });
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            {console.log("Formik Errors:", errors)}
            {console.log("Formik Values:", values)}
            <div className="row row-sm">
              <div className="col-lg-12 col-md-12 animation_fade">
                <div className="card custom-card">
                  <div className="card-body">
                    <h6 className="main-content-label mb-3">Add Automatic Email (4 Languages)</h6>

                    {/* Slug */}
                    <div className="row row-sm mb-3">
                      <div className="col-md-12 form-group">
                        <label className="text-left d-flex">Slug: <span className="requirestar">*</span></label>
                        <input
                          name="slug"
                          type="text"
                          className="form-control"
                          value={values.slug}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <span className="text-danger d-flex text-left"><ErrorMessage name="slug" /></span>
                      </div>
                    </div>

                    {/* Language Sections */}
                    {LANGUAGES.map((lang) => (
                      <div key={lang.code} className="border rounded p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                        <h6 className="mb-3">
                          <span className={`fi fi-${lang.flag} me-2`}></span>
                          {lang.name} ({lang.code.toUpperCase()})
                        </h6>

                        {/* Title */}
                        <div className="row row-sm mb-3">
                          <div className="col-md-6 form-group">
                            <label className="text-left d-flex">
                              Title ({lang.name}): <span className="requirestar">*</span>
                            </label>
                            <input
                              name={`title_${lang.code}`}
                              type="text"
                              className="form-control"
                              value={values[`title_${lang.code}`]}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <span className="text-danger d-flex text-left">
                              <ErrorMessage name={`title_${lang.code}`} />
                            </span>
                          </div>

                          {/* Subject */}
                          <div className="col-md-6 form-group">
                            <label className="text-left d-flex">
                              Subject ({lang.name}): <span className="requirestar">*</span>
                            </label>
                            <input
                              name={`subject_${lang.code}`}
                              type="text"
                              className="form-control"
                              value={values[`subject_${lang.code}`]}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <span className="text-danger d-flex text-left">
                              <ErrorMessage name={`subject_${lang.code}`} />
                            </span>
                          </div>
                        </div>

                        {/* Description */}
                        <div className="form-group">
                          <label className="text-left d-flex">
                            Description ({lang.name}): <span className="requirestar">*</span>
                          </label>
                          <CustomCkeditor
                            fieldname={`description_${lang.code}`}
                            setFieldValue={setFieldValue}
                            value={values[`description_${lang.code}`] || ""}
                            setFieldTouched={() => {}}
                          />
                          <span className="text-danger d-flex text-left">
                            {errors[`description_${lang.code}`] && touched[`description_${lang.code}`] && errors[`description_${lang.code}`]}
                          </span>
                        </div>
                      </div>
                    ))}

                    {/* Buttons */}
                    <div className="mt-4">
                      <button
                        className="btn btn-main-primary signbtn mr-2"
                        type="submit"
                        disabled={submitted || isSubmitting}
                      >
                        Submit
                      </button>
                      <button
                        className="btn ripple btn-secondary"
                        type="button"
                        onClick={() => navigate(-1)}
                        disabled={submitted || isSubmitting}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
};

export default EmailTempAdd;